/**
 * Collection schema definitions for the canvas app.
 *
 * Defines the expected field sets for each node type so the Rool backend
 * validates objects against explicit schemas rather than inferring them
 * from existing data (which leads to overly strict constraints like enums
 * on free-text fields).
 *
 * Position/size/rotation fields are prefixed with _ (hidden from AI via
 * SDK convention) and are NOT included in the schema — the server ignores
 * _-prefixed fields during schema validation.
 *
 * Call `ensureSchema(channel)` after opening or creating a space to sync
 * the schema to the server.
 */
import type { RoolChannel, FieldDef } from '@rool-dev/sdk';

// =============================================================================
// Shared field helpers
// =============================================================================

const string: FieldDef['type'] = { kind: 'string' };
const number: FieldDef['type'] = { kind: 'number' };
const maybe = (inner: FieldDef['type']): FieldDef['type'] => ({
	kind: 'maybe',
	inner,
});
const literal = (value: string): FieldDef['type'] => ({
	kind: 'literal',
	value,
});

// Auto-fit flags that can appear on any editable/resizable node
const AUTOFIT_FIELDS: FieldDef[] = [
	{ name: 'autoFitWidth', type: maybe({ kind: 'boolean' }) },
	{ name: 'autoFitHeight', type: maybe({ kind: 'boolean' }) },
	{ name: 'autoSizeText', type: maybe({ kind: 'boolean' }) },
	{ name: 'wordWrap', type: maybe({ kind: 'boolean' }) },
];

// =============================================================================
// Collection definitions
// =============================================================================

/** All collections the canvas app uses, keyed by collection name. */
const COLLECTIONS: Record<string, FieldDef[]> = {
	header: [
		{ name: 'type', type: literal('header') },
		{ name: 'content', type: string },
		{ name: 'manuallyResized', type: maybe({ kind: 'boolean' }) },
		...AUTOFIT_FIELDS,
	],

	'sticky-note': [
		{ name: 'type', type: literal('sticky-note') },
		{ name: 'content', type: string },
		{ name: 'color', type: maybe(string) },
		...AUTOFIT_FIELDS,
	],

	markdown: [
		{ name: 'type', type: literal('markdown') },
		{ name: 'content', type: string },
		{ name: 'title', type: maybe(string) },
		...AUTOFIT_FIELDS,
	],

	image: [
		{ name: 'type', type: literal('image') },
		{ name: 'url', type: string },
		{ name: 'title', type: maybe(string) },
		{ name: 'sourceFile', type: maybe(string) },
		{ name: 'sourceFileName', type: maybe(string) },
		{ name: 'sourceFileType', type: maybe(string) },
		{ name: 'currentPage', type: maybe(number) },
	],

	file: [
		{ name: 'type', type: literal('file') },
		{ name: 'url', type: string },
		{ name: 'fileName', type: string },
		{ name: 'fileType', type: string },
		{ name: 'sourceFile', type: string },
		{ name: 'sourceFileName', type: string },
		{ name: 'sourceFileType', type: string },
		{ name: 'title', type: string },
	],
};

// =============================================================================
// Schema sync
// =============================================================================

const INFERRED_COLLECTION_RE = /^collection_\d+$/;

/** Compare two FieldDef arrays for deep equality via JSON serialization. */
function fieldsMatch(a: FieldDef[], b: FieldDef[]): boolean {
	return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Ensure the space has the correct collection schemas defined.
 *
 * - Creates missing collections
 * - Alters existing collections only when fields differ
 * - Drops server-inferred collections (named `collection_0`, `collection_1`, etc.)
 *
 * Compares the local schema (from openChannel) against the desired definitions
 * and only sends server calls for actual differences. No-ops when already in sync.
 */
export async function ensureSchema(ch: RoolChannel): Promise<void> {
	const existing = ch.getSchema();

	// Drop inferred collections first so they don't conflict
	for (const name of Object.keys(existing)) {
		if (INFERRED_COLLECTION_RE.test(name)) {
			try {
				await ch.dropCollection(name);
			} catch (err) {
				console.warn(
					`[schema] Failed to drop inferred collection "${name}":`,
					err,
				);
			}
		}
	}

	// Create or alter our named collections — only when they differ
	for (const [name, fields] of Object.entries(COLLECTIONS)) {
		try {
			const existingCollection = existing[name];
			if (existingCollection) {
				// Skip if fields already match
				if (fieldsMatch(existingCollection.fields, fields)) continue;
				await ch.alterCollection(name, fields);
			} else {
				await ch.createCollection(name, fields);
			}
		} catch (err) {
			console.error(`[schema] Failed to sync collection "${name}":`, err);
		}
	}
}
